import { addMoney, getMinPrice, multiplyMoney, subtractMoney } from '@shared/lib/money';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
	GdsServiceVipSubType,
	Money,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderSelectedServices,
	useSaveOrderServicesMutation
} from '@websky/graphql';
import { TransferService } from '../../../Transfer';
import Dialog from '../../../Service/components/Dialog/Dialog';
import { useToggleable } from '../../../hooks';
import CheckoutAdditionalService from '../../index';
import { SegmentTabs } from '@shared/ui/SegmentTabs';
import { useTheme } from '../../../theme';
import Toolbar from '../../../Toolbar';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getGroundTransportationServices, getOrder } from '../../../Checkout/store/order/selectors';
import { useRenderers } from '../../../renderProps';
import { useIsMobile } from '../../../utils';
import { fillOrder } from '../../../Checkout/store/order/actions';
import { ServiceButton } from '../../types';
import { useCart } from '../../../Checkout/components/Checkout/Stepbar/hooks';
import { getSelectedServices } from '../../../Checkout/store/selectedServices/selectors';
import { useGdsServiceEventToggleable } from '../../../Checkout/components/Checkout/hooks';
import { ServiceSummary } from '../../../features/service-summary';

const Transfer: React.FC = props => {
	const { t } = useTranslation('Checkout');
	const order = useSelector(getOrder);
	const [activeSegment, setActiveSegment] = useState(order.flight.segments[0].segment.id);
	const [servicesToSave, setServicesToSave] = useState<OrderSelectedServices>(null);
	const [initialStateChanged, setInitialStateChanged] = useState<boolean>(false);
	const dispatch = useDispatch();

	const allSelectedServices = useSelector(getSelectedServices);
	const transferServices = useSelector(getGroundTransportationServices);
	const transferServicesIds = transferServices.map(({ id }) => id);

	const { isOpen, close, open } = useToggleable(false);

	useGdsServiceEventToggleable({
		services: transferServices,
		serviceTypes: [OrderAdditionalServiceGdsServiceServiceType.Vip],
		serviceSubTypes: [GdsServiceVipSubType.GroundTransportation],
		onOpen: open
	});

	const isSelected = useMemo(() => {
		const transferServicesIds = transferServices.map(({ id }) => id);

		for (const traveller of order.travellers) {
			if (
				traveller.services?.gdsServices?.services?.some(service =>
					transferServicesIds.includes(service.serviceId)
				)
			) {
				return true;
			}
		}

		return false;
	}, [order.travellers, transferServices]);

	const orderPriceToPay = useMemo(() => {
		let priceToPay: Money = {
			amount: 0,
			currency: order?.price?.total?.currency
		};

		for (const traveller of order.travellers) {
			for (const service of traveller?.services?.gdsServices?.services || []) {
				if (transferServicesIds.includes(service.serviceId)) {
					const orderService = order.additionalServices?.gdsServices?.services?.find(
						orderService => orderService.id === service.serviceId
					);

					if (orderService) {
						priceToPay = addMoney(priceToPay, multiplyMoney(orderService.price, service.segmentIds.length));
					}
				}
			}
		}

		return priceToPay;
	}, [transferServices, servicesToSave, order.travellers]);

	const minPrice = getMinPrice(transferServices, transferService => transferService.price);

	const [saveServices, { loading }] = useSaveOrderServicesMutation();

	const { Transfer: css } = useTheme('CheckoutAdditionalService');

	const handleSubmit = useCallback(async () => {
		if (!initialStateChanged) {
			close();

			return;
		}

		const result = await saveServices({
			variables: {
				params: {
					id: servicesToSave.id,
					services: servicesToSave.services.map(service => {
						const selectedService = allSelectedServices.find(
							selectedService => selectedService.travellerId === service.travellerId
						);

						return {
							travellerId: service.travellerId,
							gdsServices: {
								...service.gdsServices,
								setServices: [
									...service.gdsServices.setServices,
									...(selectedService?.gdsServices?.setServices || [])
								]
							},
							seats: selectedService?.seats
						};
					})
				}
			}
		});

		dispatch(fillOrder(result.data.SaveOrderServices));
		close();
	}, [servicesToSave, initialStateChanged]);

	const { CheckoutAdditionalService: CustomCheckoutAdditionalService } = useRenderers();
	const ServiceWrapper = CustomCheckoutAdditionalService?.CheckoutAdditionalService ?? CheckoutAdditionalService;

	const buttons = useMemo<ServiceButton[]>(() => {
		if (isSelected) {
			return [
				{
					text: t('Change'),
					action: open,
					isLoading: loading,
					buttonType: 'change',
					variant: 'optionEdit'
				}
			];
		} else {
			return [
				{
					text: t('Add'),
					action: open,
					isLoading: loading,
					buttonType: 'change',
					variant: 'action'
				}
			];
		}
	}, [isSelected, loading]);

	const priceToPayBeforeSave = useMemo<Money>(() => {
		let priceToPay: Money = {
			amount: 0,
			currency: order?.price?.total?.currency
		};

		for (const travellerServices of servicesToSave?.services ?? []) {
			for (const service of travellerServices?.gdsServices?.setServices ?? []) {
				const relatedOrderService = order.additionalServices?.gdsServices?.services?.find(
					relatedService => relatedService.id === service.serviceId
				);
				const relatedTraveller = order.travellers.find(
					traveller => traveller.id === travellerServices.travellerId
				);

				const relatedTravellerService = relatedTraveller?.services?.gdsServices?.services?.find(
					travellerService => travellerService.serviceId === relatedOrderService.id
				);

				priceToPay = addMoney(priceToPay, multiplyMoney(relatedOrderService.price, service.count));

				if (relatedTravellerService) {
					priceToPay = subtractMoney(
						priceToPay,
						multiplyMoney(relatedOrderService.price, relatedTravellerService.confirmedCount)
					);
				}
			}
		}

		return priceToPay;
	}, [servicesToSave]);

	const isMobile = useIsMobile();

	const { onDeleteItem } = useCart();

	return (
		<>
			<ServiceSummary serviceType="Transfer" onDelete={onDeleteItem}>
				{content => (
					<ServiceWrapper
						className={css.header}
						header={t('Transfer')}
						description={t('Transfer:Cheap and on time to get to your destination')}
						isSelected={isSelected}
						priceTotal={orderPriceToPay}
						isLoading={loading}
						priceFrom={minPrice}
						addClick={open}
						buttons={buttons}
					>
						{content}
					</ServiceWrapper>
				)}
			</ServiceSummary>

			{isOpen && (
				<Dialog
					isOpen={isOpen}
					dialogContentClassName={css.dialogContent}
					dialogType={'fullscreen'}
					hideHeader={true}
					onClose={close}
					content={
						<>
							<SegmentTabs
								title={t('Transfer:Choose transfer')}
								isCompact={isMobile}
								activeSegment={activeSegment}
								segments={order.flight.segments.map(segment => ({
									id: segment.segment.id,
									title: `${segment.segment.departure.airport.city.name}-${segment.segment.arrival.airport.city.name}`,
									isDisabled: false
								}))}
								onSegmentClick={setActiveSegment}
								onClose={close}
								center
								sticky
							/>

							<TransferService
								order={order}
								onChange={setServicesToSave}
								onStateChanged={setInitialStateChanged}
								activeSegment={activeSegment}
							/>

							<Toolbar
								totalPrice={priceToPayBeforeSave}
								isLoading={loading}
								onButtonClick={handleSubmit}
							/>
						</>
					}
				/>
			)}
		</>
	);
};

export default Transfer;
